import type { IconButtonProps } from '@mui/material/IconButton';

import { useCallback } from 'react';
import { usePopover } from 'minimal-shared/hooks';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Avatar from '@mui/material/Avatar';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import { Divider, Typography } from '@mui/material';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';
import { useRouter, usePathname } from 'src/routes/hooks';

import { fMobile } from 'src/utils';
import { useSelectedTenant } from 'src/layouts/provider';

import { Label } from 'src/components/label';
import { Iconify } from 'src/components/iconify';
import { AnimateBorder } from 'src/components/animate';
import { CustomPopover } from 'src/components/custom-popover';

import { useAuthContext } from 'src/auth/hooks';
import { isHqTenantCode } from 'src/auth/utils';

import { useLogout } from './sign-out-button';
import { AccountButton } from './account-button';

// ----------------------------------------------------------------------

export type AccountDrawerProps = IconButtonProps & {
  data?: {
    label: string;
    href: string;
    icon?: React.ReactNode;
    info?: React.ReactNode;
  }[];
};

export function AccountDrawer({ data = [], sx, ...other }: AccountDrawerProps) {
  const pathname = usePathname();
  const router = useRouter();
  const { user } = useAuthContext();
  const { membership, tenants, switchTenant, switching } = useSelectedTenant();
  const { handleLogout } = useLogout();
  const { open, anchorEl, onClose, onOpen } = usePopover();

  const isOnHq = pathname === paths.hq.root || pathname.startsWith(`${paths.hq.root}/`);
  const isHqTenant = isHqTenantCode(membership?.tenantCode ?? user?.tenantCode);
  const canAccessHq = user?.isSuperAdmin === true || isHqTenant;

  /** 进入平台后台：非总部会话先切回 0000 租户 */
  const handleEnterHq = useCallback(async () => {
    onClose();
    try {
      if (!isHqTenant) {
        const hqTenant = tenants.find((item) => isHqTenantCode(item.tenantCode));
        if (!hqTenant) return;
        await switchTenant(hqTenant.tenantId);
      }
      router.push(paths.hq.overview);
    } catch {
      // 租户切换失败时由请求拦截器提示
    }
  }, [isHqTenant, onClose, router, switchTenant, tenants]);

  const renderAvatar = () => (
    <AnimateBorder
      sx={{ mb: 2, p: '6px', width: 96, height: 96, borderRadius: '50%' }}
      slotProps={{
        primaryBorder: { size: 120, sx: { color: 'primary.main' } },
      }}
    >
      <Avatar
        src={user?.avatar}
        alt={user?.profile.nickname || user?.username || ''}
        sx={{ width: 1, height: 1 }}
      >
        {user?.profile.nickname?.charAt(0).toUpperCase() || user?.username?.charAt(0).toUpperCase()}
      </Avatar>
    </AnimateBorder>
  );

  const renderList = () => (
    <MenuList
      disablePadding
      sx={[
        (theme) => ({
          py: 3,
          px: 2.5,
          borderTop: `dashed 1px ${theme.vars.palette.divider}`,
          borderBottom: `dashed 1px ${theme.vars.palette.divider}`,
          '& li': { p: 0 },
        }),
      ]}
    >
      {data.map((option) => {
        const rootLabel = pathname.includes('/dashboard') ? 'Home' : 'Dashboard';
        const rootHref = pathname.includes('/dashboard') ? '/' : paths.dashboard.root;

        return (
          <MenuItem key={option.label}>
            <Link
              component={RouterLink}
              href={option.label === 'Home' ? rootHref : option.href}
              color="inherit"
              underline="none"
              onClick={onClose}
              sx={{
                p: 1,
                width: 1,
                display: 'flex',
                typography: 'body2',
                alignItems: 'center',
                color: 'text.secondary',
                '& svg': { width: 24, height: 24 },
                '&:hover': { color: 'text.primary' },
              }}
            >
              {option.icon}

              <Box component="span" sx={{ ml: 2 }}>
                {option.label === 'Home' ? rootLabel : option.label}
              </Box>

              {option.info && (
                <Label color="error" sx={{ ml: 1 }}>
                  {option.info}
                </Label>
              )}
            </Link>
          </MenuItem>
        );
      })}
    </MenuList>
  );

  return (
    <>
      <AccountButton
        onClick={onOpen}
        photoURL={
          user?.avatar ||
          (user?.profile.gender !== 'female'
            ? '/static/avatar/avatar-1.webp'
            : '/static/avatar/avatar-2.webp')
        }
        displayName={user?.profile.nickname || user?.username || ''}
        sx={sx}
        {...other}
      />

      <CustomPopover open={open} anchorEl={anchorEl} onClose={onClose}>
        <Box sx={{ p: 2, pb: 1.5, width: 200 }}>
          <Typography variant="subtitle1" noWrap>
            {user?.profile.nickname || user?.username || user?.profile.realName}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {fMobile(user?.phone)}
          </Typography>
        </Box>
        <Divider sx={{ borderStyle: 'dashed', mb: 0.5 }} />

        <MenuList>
          <MenuItem component={RouterLink} href={paths.dashboard.system.profile} onClick={onClose}>
            <Iconify icon="solar:user-rounded-bold" />
            <span>账号设置</span>
          </MenuItem>
          {canAccessHq && !isOnHq && (
            <MenuItem disabled={switching} onClick={handleEnterHq}>
              <Iconify icon="solar:monitor-bold" />
              <span>管理后台</span>
            </MenuItem>
          )}
          {isOnHq && (
            <MenuItem component={RouterLink} href={paths.dashboard.root} onClick={onClose}>
              <Iconify icon="solar:box-bold-duotone" />
              <span>进入商户后台</span>
            </MenuItem>
          )}
          <Divider sx={{ borderStyle: 'dashed' }} />
          <MenuItem
            sx={{ color: 'error.main' }}
            onClick={() => {
              handleLogout();
              onClose();
            }}
          >
            <Iconify icon="ic:round-power-settings-new" />
            <span>退出登录</span>
          </MenuItem>
        </MenuList>
      </CustomPopover>

      {/* <Drawer
        open={open}
        onClose={onClose}
        anchor="right"
        slotProps={{
          backdrop: { invisible: true },
          paper: { sx: { width: 320 } },
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            top: 12,
            left: 12,
            zIndex: 9,
            position: 'absolute',
          }}
        >
          <Iconify icon="mingcute:close-line" />
        </IconButton>

        <Scrollbar>
          <Box
            sx={{
              pt: 8,
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column',
            }}
          >
            {renderAvatar()}

            <Typography variant="subtitle1" noWrap sx={{ mt: 2 }}>
              {user?.displayName}
            </Typography>

            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }} noWrap>
              {user?.email}
            </Typography>
          </Box>

          <Box
            sx={{
              p: 3,
              gap: 1,
              flexWrap: 'wrap',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            {Array.from({ length: 3 }, (_, index) => (
              <Tooltip
                key={_mock.fullName(index + 1)}
                title={`Switch to: ${_mock.fullName(index + 1)}`}
              >
                <Avatar
                  alt={_mock.fullName(index + 1)}
                  src={_mock.image.avatar(index + 1)}
                  onClick={() => {}}
                />
              </Tooltip>
            ))}

            <Tooltip title="Add account">
              <IconButton
                sx={[
                  (theme) => ({
                    bgcolor: varAlpha(theme.vars.palette.grey['500Channel'], 0.08),
                    border: `dashed 1px ${varAlpha(theme.vars.palette.grey['500Channel'], 0.32)}`,
                  }),
                ]}
              >
                <Iconify icon="mingcute:add-line" />
              </IconButton>
            </Tooltip>
          </Box>

          {renderList()}

          <Box sx={{ px: 2.5, py: 3 }}>
            <UpgradeBlock />
          </Box>
        </Scrollbar>

        <Box sx={{ p: 2.5 }}>
          <SignOutButton onClose={onClose} />
        </Box>
      </Drawer> */}
    </>
  );
}
